import {useState} from "react";
import {useQuery} from "react-query";
import dayjs from "dayjs";
import axios from "services/axios";

function mapApiItemToRow(item, index) {
	const fullName = item?.user?.fullName || [item?.user?.firstName, item?.user?.lastName].filter(Boolean).join(" ");
	const staffName = fullName || item?.staff?.name || item?.staffName || item?.name || "-";
	const position = item?.role || item?.staff?.position || item?.position || "-";
	const dateVal = item?.checkInDate || item?.date || item?.createdAt || null;
	const checkIn = item?.checkInTime || item?.checkIn || item?.checkInAt || null;
	const checkOut = item?.checkOutTime || item?.checkOut || item?.checkOutAt || null;
	const statusVal = item?.status ?? (checkIn ? "Hadir" : "Alpa");

	// Compute duration from date+time if available, else from timestamps, else fallback
	const ciDate = item?.checkInDate;
	const ciTime = item?.checkInTime;
	const coDate = item?.checkOutDate;
	const coTime = item?.checkOutTime;
	let durationSecs = 0;
	if (ciDate && ciTime && coDate && coTime) {
		const inDT = dayjs(`${ciDate}T${ciTime}`);
		const outDT = dayjs(`${coDate}T${coTime}`);
		if (inDT.isValid() && outDT.isValid()) {
			durationSecs = Math.max(0, outDT.diff(inDT, "second"));
		}
	} else if (item?.checkInAt && item?.checkOutAt) {
		const inAt = dayjs(item.checkInAt);
		const outAt = dayjs(item.checkOutAt);
		if (inAt.isValid() && outAt.isValid()) {
			durationSecs = Math.max(0, outAt.diff(inAt, "second"));
		}
	} else {
		durationSecs = Math.max(0, Number(item?.userDurationSeconds ?? 0) || 0);
	}

	const date = dateVal ? dayjs(dateVal).format("YYYY-MM-DD") : "-";

	return {
		id: item?.id ?? index + 1,
		userId: item?.userId ?? item?.user?.id ?? null,
		idSerial: index + 1,
		staffName,
		position,
		date,
		checkIn,
		checkOut,
		status: statusVal ?? "-",
		userDurationSeconds: durationSecs,
	};
}

async function getAttendanceStaff(filter) {
	const {
		hospitalId,
		date,
		startDate,
		endDate,
		page = 1,
		limit = 10,
		search = "",
		orderBy = "checkInDate",
		sortBy = "DESC",
	} = filter || {};

	if (!hospitalId || (!date && !(startDate && endDate))) {
		return {success: true, data: {rows: [], totalItems: 0, totalPages: 0, currentPage: 1}};
	}

	const rangeFrom = startDate || date;
	const rangeTo = endDate || date;

	const params = {
		hospitalId,
		search: search || undefined,
		checkInDate: `${rangeFrom},${rangeTo}`,
		orderBy,
		sortBy,
		limit,
		page,
	};

	const {data: resp} = await axios.get("/staff/attendances", {params});

	// Expected shape: { success, message, data: { totalItems, rows, totalPages, currentPage } }
	const payload = resp || {};
	const listData = payload?.data || {};
	const apiRows = Array.isArray(listData?.rows) ? listData.rows : Array.isArray(payload?.rows) ? payload.rows : [];
	const rows = apiRows.map((it, idx) => mapApiItemToRow(it, idx));

	const totalItems = listData?.totalItems ?? payload?.totalItems ?? rows.length;
	const totalPages = listData?.totalPages ?? payload?.totalPages ?? 1;
	const currentPage = listData?.currentPage ?? payload?.currentPage ?? page;

	return {
		success: true,
		data: {
			rows,
			totalItems,
			totalPages,
			currentPage,
		},
	};
}

export function useStaffAttendanceAdminHospital(hospitalId) {
	const [filter, setFilter] = useState({
		hospitalId: hospitalId || null,
		date: dayjs().format("YYYY-MM-DD"),
		page: 1,
		limit: 10,
		search: "",
		orderBy: "checkInDate",
		sortBy: "DESC",
	});
	const fallback = {success: true, data: {rows: [], totalItems: 0, totalPages: 0, currentPage: 1}};

	const {
		data = fallback,
		isLoading,
		isError,
		error,
	} = useQuery(["attendance-staff-admin-hospital", hospitalId, filter], async () => getAttendanceStaff(filter), {
		enabled: !!(filter.hospitalId || hospitalId) && (!!filter.date || (!!filter.startDate && !!filter.endDate)),
		keepPreviousData: true,
	});

	return {data, isLoading, isError, error, filter, setFilter};
}
